import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react-native";
import { useCallback, useEffect } from "react";
import type { DefaultValues, FieldPath, Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";

import type {
  AnySchema,
  InferFormValues,
  InferValidatedValues,
  UseFormWrapperOptions,
} from "./types";

function getPaths(value: unknown, prefix = ""): string[] {
  if (value == null || typeof value !== "object") {
    return [];
  }

  return Object.entries(value).flatMap(([key, nestedValue]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    return [path, ...getPaths(nestedValue, path)];
  });
}

export function useFormWrapper<S extends AnySchema>(
  options: UseFormWrapperOptions<S>,
) {
  const {
    schema,
    defaultValues,
    mode = "onSubmit",
    reValidateMode = "onChange",
    rethrowSubmitError = false,
  } = options;

  const form = useForm<InferFormValues<S>, unknown, InferValidatedValues<S>>({
    resolver: zodResolver(schema) as unknown as Resolver<
      InferFormValues<S>,
      unknown,
      InferValidatedValues<S>
    >,
    defaultValues: defaultValues as DefaultValues<InferFormValues<S>>,
    mode,
    reValidateMode,
  });

  const {
    handleSubmit,
    reset,
    clearErrors,
    formState,
    getFieldState,
    getValues,
  } = form;

  const resetForm = useCallback(
    (next?: DefaultValues<InferFormValues<S>>) => {
      clearErrors();
      reset(next);
    },
    [clearErrors, reset],
  );

  const hasErrors = useCallback(() => {
    const errorPaths = new Set(["root", ...getPaths(getValues())]) as Set<
      FieldPath<InferFormValues<S>>
    >;

    return [...errorPaths].some((path) => getFieldState(path).error != null);
  }, [getFieldState, getValues]);

  const submit = useCallback(
    async (
      onValid: (params: {
        values: InferValidatedValues<S>;
        reset: typeof reset;
      }) => Promise<void> | void,
      onInvalid?: (errors: typeof formState.errors) => void,
    ) => {
      return handleSubmit(
        async (values) => {
          try {
            clearErrors("root");
            await onValid({ values, reset });

            if (!hasErrors()) {
              resetForm();
            }
          } catch (e) {
            Sentry.captureException(e);
            console.error("Form submission failed:", e);
            if (rethrowSubmitError) {
              throw e;
            }
          }
        },
        (errors) => {
          if (onInvalid) {
            onInvalid(errors);
          }
        },
      )();
    },
    [
      clearErrors,
      handleSubmit,
      hasErrors,
      reset,
      resetForm,
      rethrowSubmitError,
    ],
  );

  // Automatically clear root errors when any field changes
  useEffect(() => {
    const subscription = form.watch((_values, { name }) => {
      if (name && name !== "root" && form.formState.errors.root) {
        form.clearErrors("root");
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const stableForm = form as typeof form & {
    submit: typeof submit;
    resetForm: typeof resetForm;
  };

  stableForm.submit = submit;
  stableForm.resetForm = resetForm;

  return stableForm;
}
